import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock,
  Loader2,
  Send,
} from 'lucide-react';
import { usersAPI } from '../api/client';
import DateTimeField from './DateTimeField';
import { confirmResendInvite, inviteSentLabel } from '../lib/inviteSent';
import {
  isoToLocalInputValue,
  localDateTimeValue,
  nextGridLocalDateTime,
  snapLocalDateTimeForward,
} from '../lib/sendAtGrid';
import type { AdminMemberRow, ForumType, ScheduledInvite } from '../types';

interface ForumOption {
  id: number;
  name: string;
  city: string;
  state: string;
  forum_type: ForumType;
}

interface ScheduledEdit {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  forumId: number | '';
  customAmount: string;
  sendAt: string;
  personalNote: string;
  guestExpiresAt: string;
  skipOnboarding: boolean;
}

function formatSendAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function editFromInvite(s: ScheduledInvite): ScheduledEdit {
  return {
    firstName: s.first_name,
    lastName: s.last_name,
    email: s.email,
    role: s.role,
    forumId: s.forum ?? '',
    customAmount: s.custom_monthly_amount ?? '',
    sendAt: snapLocalDateTimeForward(isoToLocalInputValue(s.send_at)),
    personalNote: s.personal_note,
    guestExpiresAt: s.guest_expires_at
      ? isoToLocalInputValue(s.guest_expires_at)
      : localDateTimeValue(30 * 24 * 60),
    skipOnboarding: s.skip_onboarding ?? false,
  };
}

const ROLE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'member', label: 'Member' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'guest', label: 'Guest' },
  { value: 'facilitator', label: 'Facilitator' },
  { value: 'facilitator_admin', label: 'Facilitator + Admin' },
  { value: 'admin', label: 'Admin' },
] as const;

interface InviteQueuePanelProps {
  isAdmin: boolean;
  forums: ForumOption[];
  scheduled: ScheduledInvite[];
  setScheduled: (next: ScheduledInvite[] | ((prev: ScheduledInvite[]) => ScheduledInvite[])) => void;
  presetForumId?: number | null;
}

export default function InviteQueuePanel({
  isAdmin,
  forums,
  scheduled,
  setScheduled,
  presetForumId,
}: InviteQueuePanelProps) {
  const [statusFilter, setStatusFilter] = useState<'all' | 'scheduled' | 'awaiting'>('all');
  const [forumFilter, setForumFilter] = useState<number | ''>(presetForumId ?? '');
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [edit, setEdit] = useState<ScheduledEdit | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [scheduledError, setScheduledError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [pendingUsers, setPendingUsers] = useState<AdminMemberRow[] | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [resentIds, setResentIds] = useState<Set<number>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkAction, setBulkAction] = useState<null | 'reschedule' | 'cancel'>(null);
  const [bulkSendAt, setBulkSendAt] = useState(nextGridLocalDateTime(60));
  const [bulkWorking, setBulkWorking] = useState(false);

  useEffect(() => {
    setForumFilter(presetForumId ?? '');
  }, [presetForumId]);

  useEffect(() => {
    let cancelled = false;
    setPendingError(null);
    usersAPI
      .getAdminMembers({ pending: true })
      .then((data) => {
        if (!cancelled) setPendingUsers(data);
      })
      .catch(() => {
        if (!cancelled) setPendingError("Couldn't load the pending invite list.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const haystack = (name: string, email: string) =>
    `${name} ${email}`.toLowerCase();

  const filteredScheduled = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scheduled.filter((s) => {
      if (statusFilter === 'awaiting') return false;
      if (forumFilter !== '' && s.forum !== forumFilter) return false;
      if (roleFilter && s.role !== roleFilter) return false;
      if (q && !haystack(`${s.first_name} ${s.last_name}`, s.email).includes(q)) {
        return false;
      }
      return true;
    });
  }, [scheduled, statusFilter, forumFilter, roleFilter, search]);

  const filteredAwaiting = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (statusFilter === 'scheduled') return [];
    return (pendingUsers ?? []).filter((u) => {
      if (forumFilter !== '' && !u.forums.some((f) => f.id === forumFilter)) return false;
      if (roleFilter && u.role !== roleFilter) return false;
      if (q && !haystack(u.full_name, u.email).includes(q)) return false;
      return true;
    });
  }, [pendingUsers, statusFilter, forumFilter, roleFilter, search]);

  const filteredScheduledIds = useMemo(
    () => filteredScheduled.map((s) => s.id),
    [filteredScheduled],
  );

  useEffect(() => {
    setSelectedIds((prev) => {
      const allowed = new Set(filteredScheduledIds);
      const next = new Set([...prev].filter((id) => allowed.has(id)));
      return next.size === prev.size && [...next].every((id) => prev.has(id)) ? prev : next;
    });
  }, [filteredScheduledIds]);

  const allFilteredSelected =
    filteredScheduledIds.length > 0 &&
    filteredScheduledIds.every((id) => selectedIds.has(id));

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    setSelectedIds(allFilteredSelected ? new Set() : new Set(filteredScheduledIds));
  };

  const handleCancelScheduled = async (id: number) => {
    setCancellingId(id);
    setScheduledError(null);
    try {
      await usersAPI.cancelScheduledInvite(id);
      setScheduled((prev) => prev.filter((s) => s.id !== id));
      if (editingId === id) {
        setEditingId(null);
        setEdit(null);
      }
    } catch {
      setScheduledError("Couldn't cancel that scheduled invite.");
    } finally {
      setCancellingId(null);
    }
  };

  const startEdit = (s: ScheduledInvite) => {
    setScheduledError(null);
    setEditingId(s.id);
    setEdit(editFromInvite(s));
  };

  const saveEdit = async () => {
    if (editingId === null || !edit) return;
    setScheduledError(null);
    if (!edit.email.trim()) {
      setScheduledError('Email is required.');
      return;
    }
    const snapped = snapLocalDateTimeForward(edit.sendAt);
    const when = new Date(snapped);
    if (!edit.sendAt || Number.isNaN(when.getTime())) {
      setScheduledError('Choose a valid date and time to send the invite.');
      return;
    }
    if (when.getTime() <= Date.now()) {
      setScheduledError('The scheduled send time must be in the future.');
      return;
    }
    const amount = edit.customAmount.trim();
    if (amount !== '' && (Number.isNaN(Number(amount)) || Number(amount) < 0)) {
      setScheduledError('Enter a valid monthly amount ($0 or more).');
      return;
    }
    setSavingEdit(true);
    try {
      const updated = await usersAPI.updateScheduledInvite(editingId, {
        email: edit.email.trim(),
        first_name: edit.firstName.trim(),
        last_name: edit.lastName.trim(),
        role: edit.role,
        forum_id: edit.forumId === '' ? null : edit.forumId,
        custom_monthly_amount: amount === '' ? null : Number(amount).toFixed(2),
        personal_note: edit.personalNote.trim(),
        guest_expires_at:
          edit.role === 'guest' && edit.guestExpiresAt
            ? new Date(edit.guestExpiresAt).toISOString()
            : null,
        skip_onboarding: edit.role === 'guest' ? edit.skipOnboarding : false,
        send_at: when.toISOString(),
      });
      setScheduled((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      setEditingId(null);
      setEdit(null);
    } catch (err) {
      let detail = "Couldn't save the changes.";
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { error?: string } | undefined;
        detail = data?.error || detail;
      }
      setScheduledError(detail);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleResend = async (u: AdminMemberRow) => {
    if (!confirmResendInvite(u.full_name, u.invite_sent_at, u.invite_sent_by_name)) return;
    const userId = u.id;
    setResendingId(userId);
    setPendingError(null);
    try {
      const result = await usersAPI.resendInvite(userId);
      setPendingUsers((prev) => prev?.map((row) => (
        row.id === userId
          ? {
              ...row,
              invite_sent_at: result.invite_sent_at ?? new Date().toISOString(),
              invite_sent_by_name: result.invite_sent_by_name ?? null,
            }
          : row
      )) ?? prev);
      setResentIds((prev) => new Set(prev).add(userId));
    } catch {
      setPendingError("Couldn't resend that invite. Please try again.");
    } finally {
      setResendingId(null);
    }
  };

  const runBulk = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    setScheduledError(null);
    if (bulkAction === 'reschedule') {
      const snapped = snapLocalDateTimeForward(bulkSendAt);
      const when = new Date(snapped);
      if (!bulkSendAt || Number.isNaN(when.getTime()) || when.getTime() <= Date.now()) {
        setScheduledError('Choose a future send time on the :00 / :10 / :20 grid.');
        return;
      }
    }
    setBulkWorking(true);
    try {
      const payload =
        bulkAction === 'reschedule'
          ? {
              ids: [...selectedIds],
              action: 'reschedule' as const,
              send_at: new Date(snapLocalDateTimeForward(bulkSendAt)).toISOString(),
            }
          : { ids: [...selectedIds], action: 'cancel' as const };
      const response = await usersAPI.bulkScheduledInvites(payload);
      const failed = response.results.filter((r) => r.status === 'failed');
      if (bulkAction === 'cancel') {
        const cancelled = new Set(
          response.results.filter((r) => r.status === 'cancelled').map((r) => r.id),
        );
        setScheduled((prev) => prev.filter((s) => !cancelled.has(s.id)));
      } else {
        const updatedById = new Map(
          response.results
            .filter((r) => r.status === 'updated' && r.invite)
            .map((r) => [r.id, r.invite!]),
        );
        setScheduled((prev) => prev.map((s) => updatedById.get(s.id) ?? s));
      }
      setSelectedIds(new Set());
      setBulkAction(null);
      if (failed.length > 0) {
        setScheduledError(
          `${response.counts.failed} invite${response.counts.failed === 1 ? '' : 's'} couldn't be updated: ${failed.map((f) => f.message).join('; ')}`,
        );
      }
    } catch (err) {
      let detail = "Couldn't update those invites.";
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { error?: string } | undefined;
        detail = data?.error || detail;
      }
      setScheduledError(detail);
    } finally {
      setBulkWorking(false);
    }
  };

  const selectedCount = selectedIds.size;
  const empty =
    filteredScheduled.length === 0 &&
    (pendingUsers === null ? statusFilter === 'scheduled' : filteredAwaiting.length === 0);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        People queued to receive an invite, and people who already got one but
        haven't finished setup.
        {isAdmin ? ' As an admin you see everyone’s scheduled invites.' : ''}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <label className="block text-xs font-medium text-gray-600">
          Status
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="mt-1 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
          >
            <option value="all">Scheduled + awaiting</option>
            <option value="scheduled">Scheduled only</option>
            <option value="awaiting">Awaiting setup only</option>
          </select>
        </label>
        <label className="block text-xs font-medium text-gray-600">
          Forum
          <select
            value={forumFilter}
            onChange={(e) => setForumFilter(e.target.value ? Number(e.target.value) : '')}
            className="mt-1 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
          >
            <option value="">All forums</option>
            {forums.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs font-medium text-gray-600">
          Type
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="mt-1 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
          >
            {ROLE_OPTIONS.filter((o) => isAdmin || !['facilitator', 'facilitator_admin', 'admin'].includes(o.value)).map(
              (o) => (
                <option key={o.value || 'all'} value={o.value}>
                  {o.label}
                </option>
              ),
            )}
          </select>
        </label>
        <label className="block text-xs font-medium text-gray-600">
          Name or email
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="mt-1 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
          />
        </label>
      </div>

      {filteredScheduled.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-off-white px-3 py-2">
          <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={toggleSelectAllFiltered}
              className="rounded border-gray-300 text-navy focus:ring-gold"
            />
            Select all {filteredScheduled.length} scheduled
            {forumFilter !== '' || search || roleFilter ? ' (matching filters)' : ''}
          </label>
          {selectedCount > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <button
                type="button"
                onClick={() => {
                  setBulkSendAt(nextGridLocalDateTime(60));
                  setBulkAction('reschedule');
                }}
                className="px-3 py-1.5 rounded-lg bg-navy text-white text-xs font-semibold hover:bg-navy-dark transition"
              >
                Change send time ({selectedCount})
              </button>
              <button
                type="button"
                onClick={() => setBulkAction('cancel')}
                className="px-3 py-1.5 rounded-lg border border-red-200 text-red-700 text-xs font-semibold hover:bg-red-50 transition"
              >
                Cancel selected
              </button>
            </div>
          )}
        </div>
      )}

      {bulkAction && (
        <div className="rounded-lg border border-navy/20 bg-navy/[0.03] px-4 py-3 space-y-3">
          {bulkAction === 'reschedule' ? (
            <>
              <p className="text-sm text-navy font-medium">
                Change send time for {selectedCount} invite{selectedCount === 1 ? '' : 's'}
              </p>
              <label className="block text-xs font-medium text-gray-600">
                New send time
                <DateTimeField
                  min={nextGridLocalDateTime()}
                  stepMinutes={10}
                  value={bulkSendAt}
                  onChange={(next) => setBulkSendAt(snapLocalDateTimeForward(next))}
                  aria-label="New send time"
                  className="mt-1 w-full sm:w-auto"
                  inputClassName="px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
                />
              </label>
              <p className="text-xs text-gray-500">
                {selectedCount} invite{selectedCount === 1 ? '' : 's'} → {formatSendAt(new Date(bulkSendAt).toISOString())}.
                Times snap to :00 / :10 / :20 so they send on the dot.
              </p>
            </>
          ) : (
            <p className="text-sm text-navy font-medium">
              Cancel {selectedCount} scheduled invite{selectedCount === 1 ? '' : 's'}? They
              will not be sent.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setBulkAction(null)}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-white transition"
            >
              Never mind
            </button>
            <button
              type="button"
              onClick={() => void runBulk()}
              disabled={bulkWorking}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition disabled:opacity-50 flex items-center gap-1.5 ${
                bulkAction === 'cancel' ? 'bg-red-600 hover:bg-red-700' : 'bg-navy hover:bg-navy-dark'
              }`}
            >
              {bulkWorking && <Loader2 size={13} className="animate-spin" />}
              {bulkAction === 'cancel' ? 'Cancel invites' : 'Update send time'}
            </button>
          </div>
        </div>
      )}

      {scheduledError && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{scheduledError}</span>
        </div>
      )}
      {pendingError && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{pendingError}</span>
        </div>
      )}

      {pendingUsers === null && statusFilter !== 'scheduled' ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : empty ? (
        <div className="text-center py-8">
          <CalendarClock className="mx-auto text-gray-300" size={32} />
          <p className="text-sm text-gray-500 mt-2">
            {scheduled.length === 0 && (pendingUsers?.length ?? 0) === 0
              ? 'No scheduled invites or people awaiting setup.'
              : 'No invites match these filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredScheduled.map((s) => (
            <div key={`s-${s.id}`} className="bg-off-white rounded-lg px-3 py-2.5">
              <div className="flex items-start justify-between gap-3">
                <label className="mt-0.5 flex items-start gap-2 min-w-0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(s.id)}
                    onChange={() => toggleSelect(s.id)}
                    className="mt-1 rounded border-gray-300 text-navy focus:ring-gold"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-navy truncate">
                      {s.first_name || s.last_name
                        ? `${s.first_name} ${s.last_name}`.trim()
                        : s.email}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {s.email} · {s.role_display}
                      {s.forum_name ? ` · ${s.forum_name}` : ''}
                      {s.role === 'guest' && s.guest_expires_at
                        ? ` · access until ${formatSendAt(s.guest_expires_at)}`
                        : ''}
                      {isAdmin && s.scheduled_by_name ? ` · by ${s.scheduled_by_name}` : ''}
                    </p>
                    <p className="text-xs font-medium text-navy/80 mt-0.5 flex items-center gap-1">
                      <Clock size={12} />
                      Scheduled · sends {formatSendAt(s.send_at)}
                    </p>
                  </div>
                </label>
                <div className="flex items-center gap-3 whitespace-nowrap">
                  {editingId === s.id ? (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingId(null);
                        setEdit(null);
                        setScheduledError(null);
                      }}
                      className="text-xs font-medium text-gray-500 hover:underline"
                    >
                      Close
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEdit(s)}
                      className="text-xs font-medium text-navy hover:underline"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleCancelScheduled(s.id)}
                    disabled={cancellingId === s.id}
                    className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                  >
                    {cancellingId === s.id ? 'Cancelling…' : 'Cancel'}
                  </button>
                </div>
              </div>

              {editingId === s.id && edit && (
                <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block text-xs font-medium text-gray-600">
                    First name
                    <input
                      type="text"
                      value={edit.firstName}
                      onChange={(e) => setEdit({ ...edit, firstName: e.target.value })}
                      className="mt-1 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
                    />
                  </label>
                  <label className="block text-xs font-medium text-gray-600">
                    Last name
                    <input
                      type="text"
                      value={edit.lastName}
                      onChange={(e) => setEdit({ ...edit, lastName: e.target.value })}
                      className="mt-1 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
                    />
                  </label>
                  <label className="block text-xs font-medium text-gray-600">
                    Email
                    <input
                      type="email"
                      value={edit.email}
                      onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                      className="mt-1 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
                    />
                  </label>
                  <label className="block text-xs font-medium text-gray-600">
                    {isAdmin ? 'Role' : 'Type'}
                    <select
                      value={edit.role}
                      onChange={(e) => setEdit({ ...edit, role: e.target.value })}
                      className="mt-1 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
                    >
                      <option value="member">Member</option>
                      <option value="advisor">Advisor</option>
                      <option value="guest">Guest</option>
                      {isAdmin && (
                        <>
                          <option value="facilitator">Facilitator</option>
                          <option value="facilitator_admin">Facilitator + Admin</option>
                          <option value="admin">Admin</option>
                        </>
                      )}
                    </select>
                  </label>
                  <label className="block text-xs font-medium text-gray-600">
                    Forum
                    <select
                      value={edit.forumId}
                      onChange={(e) =>
                        setEdit({ ...edit, forumId: e.target.value ? Number(e.target.value) : '' })
                      }
                      className="mt-1 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
                    >
                      <option value="">{isAdmin ? 'No Forum yet' : 'Select…'}</option>
                      {forums.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name} ({f.city}, {f.state})
                        </option>
                      ))}
                    </select>
                  </label>
                  {edit.role === 'guest' ? (
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-gray-600">
                        Guest access expires
                        <input
                          type="datetime-local"
                          value={edit.guestExpiresAt}
                          onChange={(e) => setEdit({ ...edit, guestExpiresAt: e.target.value })}
                          className="mt-1 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
                        />
                      </label>
                      <label className="flex items-start gap-1.5 text-xs text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={edit.skipOnboarding}
                          onChange={(e) => setEdit({ ...edit, skipOnboarding: e.target.checked })}
                          className="mt-0.5 rounded border-gray-300 text-navy focus:ring-gold"
                        />
                        <span>Skip onboarding (in-meeting)</span>
                      </label>
                    </div>
                  ) : (
                    (edit.role === 'member' || edit.role === 'advisor') && (
                      <label className="block text-xs font-medium text-gray-600">
                        Monthly amount ($)
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={edit.customAmount}
                          onChange={(e) => setEdit({ ...edit, customAmount: e.target.value })}
                          placeholder="Standard rate"
                          className="mt-1 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold placeholder:text-gray-300"
                        />
                      </label>
                    )
                  )}
                  <label className="block text-xs font-medium text-gray-600 sm:col-span-2">
                    Send at
                    <DateTimeField
                      value={edit.sendAt}
                      min={nextGridLocalDateTime()}
                      stepMinutes={10}
                      onChange={(next) =>
                        setEdit({ ...edit, sendAt: snapLocalDateTimeForward(next) })
                      }
                      aria-label="Send at"
                      className="mt-1 w-full sm:w-auto"
                      inputClassName="px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
                    />
                    <span className="block mt-1 font-normal text-gray-400">
                      :00 / :10 / :20 / :30 / :40 / :50 so the email goes out on the dot.
                    </span>
                  </label>
                  <label className="block text-xs font-medium text-gray-600 sm:col-span-2">
                    Personal note
                    <textarea
                      value={edit.personalNote}
                      onChange={(e) => setEdit({ ...edit, personalNote: e.target.value })}
                      rows={2}
                      maxLength={500}
                      className="mt-1 w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
                    />
                  </label>
                  <div className="sm:col-span-2 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingId(null);
                        setEdit(null);
                        setScheduledError(null);
                      }}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-white transition"
                    >
                      Discard
                    </button>
                    <button
                      type="button"
                      onClick={() => void saveEdit()}
                      disabled={savingEdit}
                      className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:bg-navy-dark transition disabled:opacity-50 flex items-center gap-2"
                    >
                      {savingEdit && <Loader2 size={14} className="animate-spin" />}
                      Save changes
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {filteredAwaiting.map((u) => (
            <div
              key={`a-${u.id}`}
              className="flex items-center justify-between gap-3 bg-off-white rounded-lg px-3 py-2.5"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-navy truncate">{u.full_name}</p>
                <p className="text-xs text-gray-500 truncate">
                  {u.email}
                  {u.forums.length > 0 ? ` · ${u.forums.map((f) => f.name).join(', ')}` : ''}
                  {' · awaiting setup'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {inviteSentLabel(u.invite_sent_at ?? u.date_joined, u.invite_sent_by_name)}
                </p>
              </div>
              {resentIds.has(u.id) ? (
                <span className="flex items-center gap-1 text-xs font-medium text-green-600 whitespace-nowrap">
                  <CheckCircle2 size={14} />
                  Sent
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => void handleResend(u)}
                  disabled={resendingId === u.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-navy hover:bg-white transition disabled:opacity-50 whitespace-nowrap"
                >
                  {resendingId === u.id ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Send size={13} />
                  )}
                  Resend invite
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
